/**
 * A SEASON WITHOUT ITS TAPE. D-EZ-65.
 *
 * Howard, 19 Aug 2026: *"Save it all except strip the race logs. I don't need
 * to know turn by turn, but we wanna keep the stats… so that way you can have
 * year by year and totals on their cards and all that stuff, but I don't need
 * to know the race logs of past races from previous seasons."*
 *
 * ===========================================================================
 * WHAT COMES OUT, AND WHY THAT LINE AND NOT ANOTHER
 * ===========================================================================
 *
 * A finished `CareerSeason` is 139 MB of JSON. Twelve of those in one save is
 * not a save. Measured, the weight is almost entirely two things and neither of
 * them is a statistic:
 *
 *   `season.races[].race`    67 MB — the tape: every turn, every board state
 *   the turn-by-turn events  23 MB — the ledger of what happened inside a race
 *
 * ⚠ THE CUT IS `turn !== null`, AND THAT IS NOT A HEURISTIC. Every event the
 * season loop emits about a race weekend — the entry, the result, the money, the
 * winter — carries `turn: null`, because it did not happen on a turn. Every
 * event from inside a race carries the turn it happened on. So "the race log" is
 * exactly the events with a turn on them, and the statistics fold reads only the
 * ones without: `season.entry` and `season.result` are what build every driver's
 * year, and both survive.
 *
 * The RESULT of a race survives too — winner, finishing order, cautions, lead
 * changes, how long it ran. What goes is the recording of it.
 *
 * ===========================================================================
 * IT IS STILL A `CareerSeason`, ON PURPOSE
 * ===========================================================================
 *
 * The archived season keeps the type, so every reader — the record book, the
 * career page, the driver card, the all-time fold — works on it unchanged and
 * nothing has to learn a second shape. `turns` and `events` come back as empty
 * arrays, which is true: the tape is not in the archive. `archived` on the
 * season says so out loud for anything that needs to tell "no turns happened"
 * apart from "the turns are not here", and the UI uses it to stop offering a
 * replay of a race it cannot replay.
 */

import type { CareerSeason } from './careerRunner.ts';
import type { SeasonRaceResult } from '../domain/seasonPlay.ts';
import { foldSeason } from '../domain/standings.ts';
import { digestRace } from '../domain/people/memory.ts';

/**
 * ⚠ AND TWO THINGS ARE DROPPED BECAUSE THEY ARE STORED TWICE.
 *
 * `SeasonRaceResult.events` is *"this race's slice of the season ledger"* and
 * `SeasonRaceResult.standings` is the fold *"after this race"* — the same
 * objects that are already in `season.events` and `season.standingsAfterRace`.
 * Written out they cost 336 KB and 419 KB compressed PER SEASON, for facts the
 * file already contains. `restoreArchived` puts them back by slicing and
 * indexing, which is the project's own rule — derive, never duplicate — applied
 * to the one place it had never been applied: the save.
 *
 * Together with the tapes that is 12.0 MB → 2.0 MB a season, compressed.
 */
export function archiveRace(run: SeasonRaceResult): SeasonRaceResult {
  // D-EZ-108 — the rivalry engine's few hundred bytes of the tape, kept so a
  // settled season still remembers who wrecked whom. Read from the tape when it
  // is here; an already-archived race passes its digest straight through.
  const memory = digestRace(run);
  return {
    ...run,
    ...(memory ? { memory } : {}),
    // Both rebuilt on the way back in. See `restoreArchived`.
    events: [],
    standings: EMPTY_STANDINGS,
    race: {
      ...run.race,
      // The tape itself. `turnsRun` and `scheduledTurns` still say how long it
      // was, so nothing has to count an empty array to find out.
      turns: [],
      events: [],
    },
  };
}

export function archiveSeason(career: CareerSeason): CareerSeason {
  return {
    ...career,
    /*
      ⚠ THE WEEKEND WORKING PAPERS GO TOO, and this is the same cut as the tapes.
      `finance.races` is thirty-six weekends of detailed books — every line that
      produced the numbers — and with `royalties`, `renewals`, `sponsorChanges`
      and the contract state it is the largest thing left in an archived season.
      Nothing in the career reporting reads any of them: the money screens read
      `career.report` and `career.ledger`, both already folded and both kept, and
      the statistics fold reads `finance.events`, also kept.
      `finance.races[i]` is read by the LIVE Payday panel and by the
      season-in-progress save, neither of which is a past season.
    */
    finance: {
      ...career.finance,
      races: [],
      // Royalties are KEPT (53 small rows): the history layer's Most Popular
      // award reads SAG/27's fan and media points off them (D-EZ-95).
      renewals: [],
      sponsorChanges: [],
    },
    season: {
      ...career.season,
      events: career.season.events.filter((e) => e.turn === null),
      races: career.season.races.map(archiveRace),
    },
  };
}

/**
 * Put back the two fields the archive dropped as duplicates.
 *
 * ⚠ THE STANDINGS ARE MATCHED BY RACE NUMBER, NOT BY ARRAY POSITION.
 * `standingsAfterRace[i]` is documented as the championship after race i+1, and
 * a season runs 1..36 with no gaps — but indexing on the array's own order would
 * silently mis-file every race if that ever stopped being true, and a
 * championship table off by one race is the kind of wrong nobody spots. A race
 * whose fold is missing gets an empty one rather than its neighbour's.
 */
export function restoreArchived(career: CareerSeason): CareerSeason {
  const { season } = career;
  const bySlice = new Map<number, typeof season.events>();
  for (const event of season.events) {
    const slice = bySlice.get(event.raceNumber);
    if (slice) slice.push(event);
    else bySlice.set(event.raceNumber, [event]);
  }

  return {
    ...career,
    season: {
      ...season,
      races: season.races.map((run) => ({
        ...run,
        events: bySlice.get(run.raceNumber) ?? [],
        standings: season.standingsAfterRace[run.raceNumber - 1] ?? EMPTY_STANDINGS,
      })),
    },
  };
}

/** A fold of nothing — what an archived race carries where its own fold was. */
const EMPTY_STANDINGS = foldSeason([]);

/**
 * Which seasons go on the shelf, in what order.
 *
 * Pure and separate from the archiving so it can be tested with stubs: the
 * ordering rule is cheap to get wrong and enormously expensive to exercise
 * against real seasons, which are tens of megabytes each.
 *
 * De-duplicated by season number, because the caller holds the workbench and
 * the history separately and the workbench season is usually also the newest of
 * the history — a league with season 3 in it twice would put season 3 on the
 * shelf twice.
 */
export function shelfOrder(
  career: CareerSeason,
  history: readonly CareerSeason[] = [],
): CareerSeason[] {
  const byNumber = new Map<number, CareerSeason>();
  for (const season of [...history, career]) byNumber.set(season.seasonNumber, season);
  return [...byNumber.values()].sort((a, b) => a.seasonNumber - b.seasonNumber);
}
